<template>
	<div id="in-page-nav-container">
		<!-- In-page Nav-->
		<div
			ref="inPageNav"
			id="in-page-nav"
			class="in-page-nav"
			:class="{ 'in-page-nav--sticky': stickyState }"
		>
			<!-- Fund Name & CTAs (sticky only) -->
			<div class="in-page-nav__header" v-if="navHeader">
				<div class="container">
					<div class="in-page-nav__header-content">
						<p class="p1">{{ navHeader.title }}</p>
						<div class="in-page-nav__header-cta">
							<span v-if="navHeader.cta1Url" @click="emitClick('cta1')">
								<CTA
									:url="navHeader.cta1Url"
									:text="navHeader.cta1"
									:targetBlank="true"
									:aria-hidden="navHeader.cta1AriaHidden ? true : false"
									:tabindex="navHeader.cta1AriaHidden ? '-1' : '0'"
								>
									<template slot="right-icon">
										<span class="icon icon-download" aria-hidden="true" />
									</template>
								</CTA>
							</span>
							<span v-if="navHeader.cta2Url" @click="emitClick('cta2')">
								<CTA
									:url="navHeader.cta2Url"
									:text="navHeader.cta2"
									:targetBlank="true"
									:aria-hidden="navHeader.cta2AriaHidden ? true : false"
									:tabindex="navHeader.cta2AriaHidden ? '-1' : '0'"
								>
									<template slot="right-icon">
										<span class="icon icon-download" aria-hidden="true" />
									</template>
								</CTA>
							</span>
							<span v-if="navHeader.cta3Url" @click="emitClick('cta3')">
								<CTA
									:url="navHeader.cta3Url"
									:text="navHeader.cta3"
									:targetBlank="true"
									:aria-hidden="navHeader.cta3AriaHidden ? true : false"
									:tabindex="navHeader.cta3AriaHidden ? '-1' : '0'"
								>
									<template slot="right-icon">
										<span class="icon icon-download" aria-hidden="true" />
									</template>
								</CTA>
							</span>
						</div>
					</div>
				</div>
			</div>
			<!-- In-page nav menu -->
			<div class="in-page-nav__menu">
				<div class="container">
					<nav
						role="navigation"
						:aria-label="`Navigation for ${
							ariaNavLabel ? ariaNavLabel : navHeader.title
						}, Same page links`"
					>
						<ul class="in-page-nav__list">
							<li
								v-for="(item, index) in computedNavItems"
								:key="item.id"
								class="in-page-nav__item"
								:class="[
									{
										'in-page-nav__item--active': item.slug === activeSection
									},
									{ 'caret-indicator-white': caretIndicatorWhite },
									{ 'caret-indicator-gray': caretIndicatorGray }
								]"
								@keydown="($event) => onKeyPress($event, index, item)"
							>
								<a
									@click.prevent="jumpToSection(item, 500, false)"
									:href="'#' + item.slug"
									:aria-label="
										item.slug === activeSection
											? `${item.title} (active)`
											: item.title
									"
									v-html="item.title"
								/>
								<span
									v-if="caretIndicatorWhite"
									class="caret-indicator-white--up"
								/>
								<span
									v-if="caretIndicatorGray"
									class="caret-indicator-gray--down"
								/>
							</li>
						</ul>
					</nav>
				</div>
			</div>
		</div>
		<!-- This auto-generates the sections that the in-page nav scrolls to. It acts as a router
    and item.component in props will determine what component goes inside-->
		<template v-for="(item, index) in computedNavItems">
			<InPageNavSection
				@computeAccordionOffset="computeAccordionOffset"
				@closeAccordions="closeAccordions"
				@setActiveSection="handleActive"
				@inPageNavSectionUpdated="onSectionUpdated"
				:index="index"
				:item="item"
				:key="item.id"
				:ref="item.slug"
				:tabindex="enterClicked === true ? '0' : '-1'"
				:navHeader="navHeader"
				:urlHash="urlHash"
				:caretIndicatorWhite="caretIndicatorWhite"
				:caretIndicatorGray="caretIndicatorGray"
			/>
		</template>

		<v-breakpoint @small="handleSmall" v-model="model" />
	</div>
</template>

<script>
import InPageNavSection from "./InPageNavSection";
import { VBreakpoint, Model } from "@vg-faset/core";
import CTA from "../cta/CTA";
import { adobeLaunch } from "vg-fas-adobe-launch";
import debounce from "lodash/debounce";

/**
 * An in-page navigation component.
 *
 * ## Props details
 *
 * Prop details for props that accept objects.
 *
 * ### `navItems`
 *
 * The `navItems` prop is an arry of objects that contain the navigation items. Each item object can accept the following:
 *
 * ```javascript
 * navItems: [
 * 	{
 * 		slug {string} this will populate the # in the url
 * 		title {string} The text that shows up in the nav
 * 		component {vue component} the component that load into the section
 * 		componentProps {object} props for component
 * 		bg {string} applies a css class `bg--${item.bg}` so you can alter the bg if you need to
 * 		noBottomPaddingSmall {boolean} removes default bottom padding on small when designs call against it
 * 		noTopPaddingSmall {boolean} removes default top padding on small when designs call against it
 * 		noTopPadding {boolean} removes padding above section at all BPs
 * 		noBottomPadding {boolean} removes padding below section at all BPs
 * 	}
 * ]
 * ```
 *
 * ### `navHeader`
 *
 * The `navHeader` prop is an object of properties to populate the navigation header that appears when you begin scrolling and it becomes sticky.
 *
 * ```javascript
 * navHeader: {
 * 	title: On-screen header which appears when sticky nav is in view
 * 	cta1: Link label for CTA 1
 * 	cta1Url: URL of CTA 1
 * 	cta1AriaHidden: Set to true to hide from screen reader. This can be useful to avoid CTA being read multiple times if it's already on-screen in another location
 * 	cta2: Link label for CTA 2
 * 	cta2Url: URL of CTA 2
 * 	cta2AriaHidden: Set to true to hide from screen reader. This can be useful to avoid CTA being read multiple times if it's already on-screen in another location
 * }
 * ```
 */
export default {
	components: {
		InPageNavSection,
		VBreakpoint,
		CTA
	},
	props: {
		/**
		 * An array of nav item objects. See the documentation above for details.
		 */
		navItems: {
			type: Array,
			default: () => []
		},

		/**
		 * An object containing navigation header properties. See the documentation above for details.
		 */
		navHeader: {
			type: Object,
			default: () => {}
		},

		/**
		 * This is required for accessibility if navHeader.title is not defined. Used to set aria-label of nav element.
		 */
		ariaNavLabel: {
			type: String,
			default: ""
		},

		/**
		 * optional - Used to open the first accordian section on page load (if there is no url hash)
		 */
		openFirstSection: {
			type: Boolean,
			default: false
		},

		/**
		 * optional - turns on the white caret indicator option.
		 */
		caretIndicatorWhite: {
			type: Boolean,
			default: false
		},

		/**
		 * optional - turns on the gray caret indicator option.
		 */
		caretIndicatorGray: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			// computedNavItems: null,
			stickyState: false,
			autoScrolling: false,
			accordionOffset: 0,
			accordionLastIndex: 0,
			accordionClosed: true,
			lastClicked: null,
			model: new Model(),
			urlPath: window.location.pathname,
			activeSection: "initial",
			urlHash: window.location.hash ? window.location.hash.substring(1) : "",
			isScrolling: null,
			enterClicked: false
		};
	},
	computed: {
		/* navItems with an 'openFirstSection' value added to the object */
		computedNavItems() {
			const computedNavItems = this.navItems;

			if (this.openFirstSection && !this.lastClicked) {
				computedNavItems[0].openFirstSection = true;
			}

			return computedNavItems;
		},
		/* Hash of the first section in the nav */
		firstSectionHash() {
			return this.computedNavItems[0].slug ? this.computedNavItems[0].slug : "";
		},
		/* Hash of the last section in the nav */
		lastSectionHash() {
			return this.computedNavItems[this.computedNavItems.length - 1].slug
				? this.computedNavItems[this.computedNavItems.length - 1].slug
				: "";
		}
	},
	methods: {
		/* Updates the nav padding so that it doesn't overlap sticky global nav */
		updateNavPadding() {
			this.$refs.inPageNav.style.paddingTop = this.getStickyNavOffset() + "px";
		},
		/* Get height of VGN sticky nav bar */
		getStickyNavOffset() {
			return document.getElementsByClassName("vgn-navBar-sticky")[0]
				? document.getElementsByClassName("vgn-navBar-sticky")[0].offsetHeight
				: 0;
		},
		/* Triggers scroll and sets active state on in-page nav when clicking */
		jumpToSection(clickedItem, duration, isUrlHash, sectionUpdate) {
			if (clickedItem.slug) {
				this.$scrollTo("#" + clickedItem.slug, {
					offset: 1 - this.getStickyNavOffset(),
					duration: duration,
					onStart: this.scrollStart,
					onDone: this.scrollDone,
					cancelable: false
				});

				this.replaceState(this.urlPath, `#${clickedItem.slug}`);

				if (!sectionUpdate) {
					if (this.activeSection !== clickedItem.slug && !isUrlHash) {
						adobeLaunch.trackSubpageViewed(clickedItem.title, "Y");
					} else {
						adobeLaunch.trackSubpageViewed(clickedItem.title, "N");
					}
				}

				this.activeSection = clickedItem.slug;
				this.lastClicked = clickedItem;
			}
		},
		/* Throttles function to make in-page nav sticky on scroll */
		handleScroll() {
			if (!window.requestAnimationFrame) {
				setTimeout(this.handleSticky, 300);
			} else {
				window.requestAnimationFrame(() => this.handleSticky());
			}
		},
		/* Makes in-page nav sticky and computes offset for smooth scrolling */
		handleSticky() {
			if (this.model.isMedium || this.model.isLarge) {
				let offsetTop = this.$el.getBoundingClientRect().top;

				if (offsetTop <= this.getStickyNavOffset() && !this.stickyState) {
					this.stickyState = true;

					if (
						!this.autoScrolling &&
						this.firstSectionHash &&
						!window.location.hash
					) {
						this.replaceState(this.urlPath, `#${this.firstSectionHash}`);
					}

					this.updateNavPadding();

					this.$refs.inPageNav.nextElementSibling.style.paddingTop =
						this.$refs.inPageNav.offsetHeight + "px";
				} else if (offsetTop > this.getStickyNavOffset() && this.stickyState) {
					this.stickyState = false;

					if (!this.autoScrolling) {
						this.replaceState(this.urlPath);
					}
					this.$refs.inPageNav.nextElementSibling.style.paddingTop = "0";
				}

				// Add and remove hash when scrolling past nav container
				if (!this.autoScrolling) {
					let offsetBottom = this.$el.getBoundingClientRect().bottom;

					if (
						offsetBottom < this.$refs.inPageNav.offsetHeight &&
						!this.outOfView
					) {
						this.replaceState(this.urlPath);
						this.outOfView = true;
					} else if (
						offsetBottom >= this.$refs.inPageNav.offsetHeight &&
						this.outOfView &&
						this.lastSectionHash
					) {
						this.replaceState(this.urlPath, `#${this.lastSectionHash}`);
						this.outOfView = false;
					}
				}
			}
		},
		/* Sets active state on in-page nav while scrolling, unless the scrolling is from a click */
		handleActive: debounce(function (slug) {
			if (this.model.isMedium || this.model.isLarge) {
				if (!this.autoScrolling) {
					for (let item of this.computedNavItems) {
						if (item.slug && item.slug === slug && item !== this.lastClicked) {
							this.lastClicked = item;
							this.replaceState(this.urlPath, `#${item.slug}`);
						}
						if (item.slug && item.slug === slug) {
							this.activeSection = slug;
						}
					}
				}
			}
		}, 100),
		/* Boolean for anchor link smooth scrolling */
		scrollStart() {
			this.autoScrolling = true;
		},
		scrollDone() {
			this.autoScrolling = false;
			// Focus the header after scrolling is done;
			// the grabFocus method lives on InPageNavSection
			this.$refs[this.lastClicked.slug][0].grabFocus();
		},
		/* Loop through child sections and close accordions */
		closeAccordions() {
			for (let item of this.computedNavItems) {
				this.$refs[item.slug][0].accordionActive = false;
				this.$refs[item.slug][0].fixedHeader = false;
				this.$refs[item.slug][0].accordionStickToBottom = false;
			}
		},
		/* Check how much to offset accordion scrolling if a previous accordion needs to close */
		computeAccordionOffset(height, index) {
			this.accordionOffset = height;
			this.accordionLastIndex = index;
		},
		/* Make sure padding is removed from top section when switching to small while nav is sticky (edge case) */
		handleSmall() {
			this.$refs.inPageNav.nextElementSibling.style.paddingTop = "0";
		},
		activateInitSection(sectionUpdate) {
			//If URL has a hash, jump to that section
			//Otherwise, set active section to first section
			if (this.urlHash) {
				for (let item of this.computedNavItems) {
					if (item.slug && item.slug === this.urlHash) {
						this.jumpToSection(item, -1, true, sectionUpdate);
					}
				}
			} else if (this.computedNavItems[0].slug) {
				this.activeSection = this.computedNavItems[0].slug;
			}
		},
		onSectionUpdated(slug) {
			if (this.urlHash === slug) {
				// gives other sections a chance to load before navigating
				setTimeout(() => {
					this.activateInitSection(true);
				}, 1000);
			}
		},
		emitClick(item) {
			this.$emit(`${item}Clicked`);
		},
		replaceState(path = "", hash = "") {
			history.replaceState({}, "", `${path}${window.location.search}${hash}`);
		},

		onKeyPress(event, index, item) {
			event.keyCode === 13 ? (this.enterClicked = true) : null;
			if (event.keyCode === 9 && this.enterClicked === true) {
				event.preventDefault();
				this.$refs[item.slug][0].$el.focus();
				this.enterClicked = false;
			}
		}
	},
	beforeCreate() {
		window.scrollTo(0, 0);
	},
	/* Bind to scroll event and compute nav offset on creation */
	created() {
		window.addEventListener("scroll", this.handleScroll);
		window.addEventListener("resize", this.updateNavPadding);
	},
	destroyed() {
		window.removeEventListener("scroll", this.handleScroll);
		window.removeEventListener("resize", this.updateNavPadding);
	},
	watch: {
		activeSection: function (newVal) {
			let currentNavItem;

			window.clearTimeout(this.isScrolling); //When active section changes, clear timer

			if ((this.model.isMedium || this.model.isLarge) && !this.autoScrolling) {
				currentNavItem = this.computedNavItems.find((item) => {
					return item.slug === newVal;
				});

				//If the user stays in one section for 5 seconds, track subpage viewed
				this.isScrolling = setTimeout(function () {
					adobeLaunch.trackSubpageViewed(currentNavItem.title, "N");
				}, 5000);
			}
		}
	}
};
</script>

<style lang="scss">
.in-page-nav {
	@include from-md {
		.in-page-nav__header-content,
		.in-page-nav__list {
			transition: 0.2s ease height;
		}

		&--sticky {
			box-shadow: 0 8px 10px -1px rgba(0, 0, 0, 0.12);
			position: fixed;
			top: 0;
			width: 100%;
			z-index: 10;

			.in-page-nav__header-content,
			.in-page-nav__list {
				height: $nav-height-sticky / 2;
			}
		}

		&__menu {
			background-color: $color--dark-gray;
		}

		&__header {
			background-color: $color--white;
		}

		&__header-content {
			align-items: center;
			background-color: $color--white;
			display: flex;
			height: 0;
			justify-content: space-between;
			overflow: hidden;

			p {
				margin-bottom: 0;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}

		&__header-cta {
			white-space: nowrap;

			a {
				display: inline;
				margin-left: 25px;
			}
		}

		&__list {
			display: flex;
			height: $nav-height;
			list-style: none;
		}

		&__item {
			border-bottom: 6px solid $color--dark-gray;
			display: flex;
			margin-bottom: 0;
			margin-left: 0;
			padding-left: 0;
			transition: background-color ease 0.2s, border-bottom ease 0.2s;

			&:hover {
				a {
					color: $color--light-gray;
				}
			}

			&--active {
				background-color: $color--black;
				border-bottom: 6px solid $color--mid-gray;

				&:hover {
					a {
						color: $color--white;
					}
				}
			}

			a {
				align-items: center;
				color: $color--white;
				display: flex;
				font-size: 13px;
				line-height: 1.15;
				margin-top: 10px;
				padding: 0 12px;

				&:focus {
					color: $color--light-gray;
				}

				@include from-lg {
					font-size: 15px;
					padding: 0 25px;
				}
			}

			&.in-page-nav__item--active {
				a:focus {
					color: $color--white;
				}
			}
		}

		.caret-indicator-white {
			&.in-page-nav__item--active {
				position: relative;
				border-bottom: 6px solid $color--black;

				$size: 12px;
				.caret-indicator-white--up {
					position: absolute;
					width: 0;
					height: 0;
					bottom: -6px;
					left: 50%;
					transform: translate(-50%);
					border-right: $size solid transparent;
					border-bottom: $size solid $color--white;
					border-left: $size solid transparent;
				}
			}
		}

		.caret-indicator-gray {
			&.in-page-nav__item--active {
				position: relative;

				$size: 12px;
				.caret-indicator-gray--down {
					position: absolute;
					width: 0;
					height: 0;
					bottom: -16px;
					left: 50%;
					transform: translate(-50%);
					border-right: $size solid transparent;
					border-top: $size solid $color--mid-gray;
					border-left: $size solid transparent;
					z-index: 1;
				}
			}
		}
	}

	@include to-md {
		display: none;
	}
}
</style>
